import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { Card } from '@reflex/ui/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@reflex/ui/components/ui/collapsible';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { RequireSuperAdmin } from '@/components/auth/RequireSuperAdmin';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import {
  useSecretRotationProgress,
  type SecretRotationProgressEntry,
} from '@/hooks/useSecretRotationProgress';
import type { SecretManagerStatus, SecretRotationOutcome } from '@/lib/types';

/**
 * Admin → Secrets → Storage detail page.
 *
 * Surfaces the active secret manager, primary/retired key fingerprints,
 * and the no-downtime key-rotation runbook. Wraps the whole thing in a
 * {@link RequireSuperAdmin} guard so non-admins land on the standard
 * "access denied" page rather than seeing a half-rendered shell.
 *
 * Why this lives outside `AdminSecretsPage`: rotation is a destructive
 * key-management workflow that we want operators to navigate to
 * deliberately. The compact summary card on the secrets page links
 * here so the in-place page stays focused on day-to-day credential
 * management.
 */
export function SecretStorageDetailPage() {
  return (
    <RequireSuperAdmin>
      <SecretStorageDetailContent />
    </RequireSuperAdmin>
  );
}

function SecretStorageDetailContent() {
  const queryClient = useQueryClient();
  const orgRoutes = useOrgRoutes();
  const statusQuery = useQuery({
    queryKey: queryKeys.secretManagerStatus,
    queryFn: api.getSecretManagerStatus,
  });

  const progress = useSecretRotationProgress();

  const rotateMutation = useMutation({
    mutationFn: () => api.rotateSecrets(),
    onMutate: () => {
      progress.reset();
    },
    onSuccess: (result) => {
      const failedCount = result.failed.length;
      if (failedCount === 0) {
        toast.success('Secret rotation completed', {
          description: `${result.rotated} rotated · ${result.alreadyCurrent} already current`,
        });
      } else {
        toast.error(
          `Rotation finished with ${failedCount} failure${failedCount === 1 ? '' : 's'}`,
          {
            description: `${result.rotated} rotated · ${result.alreadyCurrent} already current`,
          },
        );
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.secretManagerStatus });
    },
    onError: (err: Error) => {
      toast.error('Failed to rotate secrets', { description: err.message });
    },
  });

  // Refresh the status snapshot once rotation completes so the
  // "on-primary" counter reflects the new reality. The mutation's
  // own onSuccess already invalidates, but the WS-driven completion
  // can arrive after the HTTP reply lands and we want the counters to
  // be in sync regardless of which finishes first.
  useEffect(() => {
    if (progress.result) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.secretManagerStatus });
    }
  }, [progress.result, queryClient]);

  const status = statusQuery.data;
  const isRotating = rotateMutation.isPending || progress.active;

  return (
    <PageLayout maxWidth="4xl">
      <PageHeader
        title="Secret Storage"
        description="Backend that holds at-rest secrets, plus the key-rotation runbook."
        docs="administration/security#secrets-header"
        actions={
          <Button variant="ghost" size="sm" asChild>
            <Link to={orgRoutes.secrets} aria-label="Back to secrets">
              <ArrowLeft className="mr-1 h-4 w-4" /> Back
            </Link>
          </Button>
        }
      />
      <AsyncContent
        isLoading={statusQuery.isLoading}
        isError={statusQuery.isError}
        loadingMessage="Loading storage details…"
        errorMessage="Failed to load secret manager status."
      >
        {status && (
          <div className="space-y-6" data-testid="secret-storage-detail">
            <ManagerOverview status={status} />
            {status.kind === 'plaintext' && <PlaintextWarning />}
            {status.kind === 'encrypted' && (
              <>
                <RotationRunbook />
                <RotationPanel
                  status={status}
                  isRotating={isRotating}
                  onRotate={() => rotateMutation.mutate()}
                  progress={progress}
                />
              </>
            )}
          </div>
        )}
      </AsyncContent>
    </PageLayout>
  );
}

function ManagerOverview({ status }: { status: SecretManagerStatus }) {
  const tone =
    status.kind === 'encrypted'
      ? 'text-status-success'
      : status.kind === 'plaintext'
        ? 'text-status-accent-orange'
        : 'text-muted-foreground';
  return (
    <Card className="space-y-3 p-4" data-testid="manager-overview">
      <div className="flex items-center gap-3">
        {status.kind === 'plaintext' ? (
          <ShieldAlert className={`h-5 w-5 ${tone}`} />
        ) : (
          <ShieldCheck className={`h-5 w-5 ${tone}`} />
        )}
        <div className="flex-1">
          <SectionLabel as="h3" className="mb-1">
            Active manager
          </SectionLabel>
          <div className="font-mono text-sm" data-testid="manager-id">
            {status.id}
          </div>
        </div>
        <Badge variant="secondary" className="text-xs font-normal capitalize">
          {status.kind}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
        <Stat label="Total secrets" value={status.rotation.totalSecrets} />
        {status.rotation.onPrimary !== undefined && (
          <Stat
            label="On primary key"
            value={status.rotation.onPrimary}
            data-testid="stat-on-primary"
          />
        )}
        {status.rotation.needsRotation !== undefined && (
          <Stat
            label="Needs rotation"
            value={status.rotation.needsRotation}
            tone={status.rotation.needsRotation > 0 ? 'warn' : 'ok'}
            data-testid="stat-needs-rotation"
          />
        )}
      </div>
    </Card>
  );
}

function Stat({
  label,
  value,
  tone,
  ...rest
}: {
  label: string;
  value: number;
  tone?: 'ok' | 'warn';
  'data-testid'?: string;
}) {
  const valueClass =
    tone === 'warn' ? 'text-status-accent-orange' : tone === 'ok' ? 'text-status-success' : '';
  return (
    <div {...rest}>
      <SectionLabel>{label}</SectionLabel>
      <div className={`text-lg font-semibold ${valueClass}`}>{value}</div>
    </div>
  );
}

function PlaintextWarning() {
  return (
    <Card className="border-status-accent-orange/40 bg-status-accent-orange/5 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-status-accent-orange" />
        <div className="space-y-1 text-sm">
          <div className="font-medium">Plaintext secret storage</div>
          <p className="text-muted-foreground">
            Secrets are stored unencrypted in the database. Rotation is not supported. Set
            <code className="mx-1 rounded bg-muted px-1 text-xs">TOKEN_ENCRYPTION_KEY</code>
            and restart the server to switch to the encrypted manager.
          </p>
        </div>
      </div>
    </Card>
  );
}

/**
 * Operator runbook for the no-downtime rotation workflow. Lives on the
 * Storage page so the explanation sits next to the button it controls
 * — the most common source of confusion is that "Rotate now" only
 * re-encrypts existing rows under the *current* primary key, while the
 * key swap itself is an env-var + restart step the operator owns. This
 * walks through both halves explicitly.
 *
 * Defaults to closed so the rotation panel is the first thing in
 * view; operators expand the runbook on demand when they need a
 * refresher.
 */
function RotationRunbook() {
  return (
    <Collapsible>
      <Card className="p-4" data-testid="rotation-runbook">
        <CollapsibleTrigger
          className="group flex w-full items-center justify-between gap-3 text-left"
          aria-label="How key rotation works"
        >
          <div>
            <SectionLabel as="h3">How key rotation works</SectionLabel>
            <p className="mt-1 text-xs text-muted-foreground">
              The Rotate button only re-encrypts existing rows. Generating and swapping the key is
              an env-var + restart step.
            </p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-90" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ol className="mt-4 space-y-4 border-t pt-4 text-sm" data-testid="rotation-runbook-steps">
            <RunbookStep
              n={1}
              title="Generate a new 32-byte key"
              body={
                <>
                  Run once, off-server. Treat the output as the new primary, <Code>K_new</Code>.
                </>
              }
              code={`node -e "console.log(require('crypto').randomBytes(32).toString('hex'))"`}
            />
            <RunbookStep
              n={2}
              title="Set both env vars and restart"
              body={
                <>
                  The previous primary becomes a decrypt-only sibling. The manager will keep reading
                  rows under the old key while writing new envelopes under <Code>K_new</Code>, with
                  no downtime.
                </>
              }
              code={`TOKEN_ENCRYPTION_KEY          = K_new
TOKEN_ENCRYPTION_KEYS_RETIRED = K_old`}
            />
            <RunbookStep
              n={3}
              title="Click Rotate now"
              body={
                <>
                  Walks every row, decrypts under whichever key it's currently under, re-encrypts
                  under <Code>K_new</Code> in a per-row transaction. Live progress streams over
                  WebSocket. Safe to interrupt: re-running the pass picks up where it left off.
                </>
              }
            />
            <RunbookStep
              n={4}
              title="Drop the retired key (optional cleanup)"
              body={
                <>
                  After step 3 finishes with zero failures, no row references <Code>K_old</Code> any
                  more. Removing it from <Code>TOKEN_ENCRYPTION_KEYS_RETIRED</Code> and restarting
                  takes it out of the in-memory keyring entirely.
                </>
              }
              code={`TOKEN_ENCRYPTION_KEY          = K_new
TOKEN_ENCRYPTION_KEYS_RETIRED =`}
            />
            <li className="rounded-md border border-dashed border-muted px-3 py-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Why two env vars?</span> Reads have to
              keep working across the restart, so both keys must be trusted for decryption while a
              rotation pass is in flight. Once the table is fully migrated, the retired key is
              unreferenced and safe to drop.
            </li>
          </ol>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

function RunbookStep({
  n,
  title,
  body,
  code,
}: {
  n: number;
  title: string;
  body: React.ReactNode;
  code?: string;
}) {
  return (
    <li className="flex gap-3" data-testid={`runbook-step-${n}`}>
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
        {n}
      </span>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="font-medium">{title}</div>
        <p className="text-xs text-muted-foreground">{body}</p>
        {code && (
          <pre className="overflow-x-auto rounded-md bg-muted px-3 py-2 font-mono text-[11px] leading-relaxed text-foreground">
            {code}
          </pre>
        )}
      </div>
    </li>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">{children}</code>;
}

function RotationPanel({
  status,
  isRotating,
  onRotate,
  progress,
}: {
  status: SecretManagerStatus;
  isRotating: boolean;
  onRotate: () => void;
  progress: ReturnType<typeof useSecretRotationProgress>;
}) {
  const primaryKeyId = status.rotation.primaryKeyId;
  const retiredKeyIds = status.rotation.retiredKeyIds ?? [];
  const total = status.rotation.totalSecrets;
  const onPrimary = status.rotation.onPrimary ?? 0;
  const liveProcessed = progress.latest?.processed ?? null;
  const liveTotal = progress.latest?.total ?? null;
  const baseProcessed = liveProcessed ?? onPrimary;
  const baseTotal = liveTotal ?? total;
  const percent =
    baseTotal === 0 ? 100 : Math.min(100, Math.round((baseProcessed / baseTotal) * 100));

  return (
    <div className="space-y-4">
      <Card className="space-y-4 p-4" data-testid="rotation-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <SectionLabel as="h3">Key rotation</SectionLabel>
          </div>
          <Button
            type="button"
            onClick={onRotate}
            disabled={isRotating || !status.rotation.supported}
            data-testid="rotate-button"
          >
            {isRotating ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                Rotating…
              </>
            ) : (
              'Rotate now'
            )}
          </Button>
        </div>
        {primaryKeyId && (
          <KeyRow
            label="Primary"
            keyId={primaryKeyId}
            description="New writes are encrypted under this key. Rotation re-encrypts every other row to match."
            tone="primary"
            data-testid="key-row-primary"
          />
        )}
        {retiredKeyIds.length === 0 ? (
          <div className="rounded-md border border-dashed border-muted px-3 py-2 text-xs text-muted-foreground">
            No retired keys configured. Set{' '}
            <code className="rounded bg-muted px-1">TOKEN_ENCRYPTION_KEYS_RETIRED</code> to a
            comma-separated list of hex keys to enable no-downtime rotation.
          </div>
        ) : (
          <div className="space-y-2">
            <SectionLabel as="span" className="text-[11px]">
              Retired keys (decrypt only)
            </SectionLabel>
            {retiredKeyIds.map((id) => (
              <KeyRow
                key={id}
                label="Retired"
                keyId={id}
                description="Still trusted for decryption. Drop after rotation completes."
                tone="retired"
                data-testid={`key-row-retired-${id}`}
              />
            ))}
          </div>
        )}
        <ProgressBar percent={percent} processed={baseProcessed} total={baseTotal} />
        <RotationFooter status={status} progress={progress} isRotating={isRotating} />
      </Card>
      {(progress.entries.length > 0 || progress.result) && <ProgressLog progress={progress} />}
    </div>
  );
}

function KeyRow({
  label,
  keyId,
  description,
  tone,
  ...rest
}: {
  label: string;
  keyId: string;
  description: string;
  tone: 'primary' | 'retired';
  'data-testid'?: string;
}) {
  return (
    <div className="flex items-start gap-3" {...rest}>
      <Badge
        variant="secondary"
        className={`shrink-0 text-xs font-normal ${
          tone === 'primary' ? 'text-status-success' : 'text-muted-foreground'
        }`}
      >
        {label}
      </Badge>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-sm" data-testid="key-fingerprint">
          {keyId}
        </div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
    </div>
  );
}

function ProgressBar({
  percent,
  processed,
  total,
}: {
  percent: number;
  processed: number;
  total: number;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{percent}% on primary</span>
        <span data-testid="progress-counter">
          {processed} / {total}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full bg-status-success transition-all"
          style={{ width: `${percent}%` }}
          data-testid="progress-bar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          role="progressbar"
          aria-label="Re-encryption progress"
        />
      </div>
    </div>
  );
}

function RotationFooter({
  status,
  progress,
  isRotating,
}: {
  status: SecretManagerStatus;
  progress: ReturnType<typeof useSecretRotationProgress>;
  isRotating: boolean;
}) {
  if (isRotating && progress.latest) {
    return (
      <div className="text-xs text-muted-foreground" data-testid="rotation-status">
        Rotating <span className="font-mono">{progress.latest.name}</span> (
        {progress.latest.processed} / {progress.latest.total})…
      </div>
    );
  }
  if (progress.result) {
    const { rotated, alreadyCurrent, failed } = progress.result;
    return (
      <div className="space-y-1 text-xs" data-testid="rotation-result">
        <div className="text-muted-foreground">
          Last pass: <span className="text-status-success">{rotated} rotated</span>,{' '}
          {alreadyCurrent} already current
          {failed.length > 0 ? (
            <>
              {', '}
              <span className="text-status-accent-orange">{failed.length} failed</span>
            </>
          ) : null}
          .
        </div>
      </div>
    );
  }
  if (status.rotation.needsRotation && status.rotation.needsRotation > 0) {
    return (
      <div className="text-xs text-muted-foreground" data-testid="rotation-hint">
        {status.rotation.needsRotation} secret{status.rotation.needsRotation === 1 ? '' : 's'} are
        still encrypted under a retired key. Run a rotation pass to bring them onto the primary key.
      </div>
    );
  }
  return (
    <div className="text-xs text-muted-foreground" data-testid="rotation-hint">
      All secrets are encrypted under the primary key. Re-running rotation is a fast no-op.
    </div>
  );
}

function ProgressLog({ progress }: { progress: ReturnType<typeof useSecretRotationProgress> }) {
  return (
    <Card className="p-4" data-testid="rotation-log">
      <SectionLabel as="h3" className="mb-2">
        Rotation log
      </SectionLabel>
      <ul className="max-h-64 space-y-1 overflow-y-auto font-mono text-xs">
        {progress.entries.map((entry, idx) => (
          <ProgressLogRow key={`${entry.processed}-${entry.name}-${idx}`} entry={entry} />
        ))}
        {progress.result && (
          <li className="pt-1 text-muted-foreground" data-testid="rotation-log-complete">
            ✓ pass complete · rotated {progress.result.rotated} · already current{' '}
            {progress.result.alreadyCurrent}
            {progress.result.failed.length > 0 ? ` · failed ${progress.result.failed.length}` : ''}
          </li>
        )}
      </ul>
    </Card>
  );
}

function ProgressLogRow({ entry }: { entry: SecretRotationProgressEntry }) {
  const tone = outcomeTone(entry.outcome);
  return (
    <li className={`flex items-center gap-2 ${tone}`}>
      <OutcomeIcon outcome={entry.outcome} className="h-3.5 w-3.5 shrink-0" />
      <span className="shrink-0 tabular-nums text-muted-foreground">
        {entry.processed}/{entry.total}
      </span>
      <span className="truncate">{entry.name}</span>
      <span className="ml-auto shrink-0 text-muted-foreground">{entry.outcome}</span>
    </li>
  );
}

function OutcomeIcon({
  outcome,
  className,
}: {
  outcome: SecretRotationOutcome;
  className?: string;
}) {
  if (outcome === 'failed') return <XCircle className={className} />;
  return <CheckCircle2 className={className} />;
}

function outcomeTone(outcome: SecretRotationOutcome): string {
  if (outcome === 'rotated') return 'text-status-success';
  if (outcome === 'already-current') return 'text-muted-foreground';
  return 'text-status-accent-orange';
}
